"use client";

import {usePathname} from "next/navigation";
import React, {
   createContext,
   useCallback,
   useContext,
   useEffect,
   useMemo,
   useRef,
   useState,
   useSyncExternalStore,
} from "react";
import {useHydrationSafeFeatureFlagEnabled} from "@/hooks/useHydrationSafeFeatureFlagEnabled";
import {useGailGptChatAccess} from "@/hooks/useGailGptChatAccess";
import {useOrg} from "@/hooks/useOrg";
import {getPinnedConversationRouting, subscribeConversationRouting} from "@/services/wingman/routing";
import {isGailGptRoute, workflowBuilderRouteId} from "@/utils/common/constants/routes";

export const DOCK_FEATURE_FLAG = "ui-chat-is-life";

const DOCK_STORAGE_KEY = "gailgpt-dock-state";

export type DockSide = "left" | "right";

export type StoredDockState = {
   isOpen: boolean;
   conversationId: string | null;
   side: DockSide;
};

const parseDockSide = (value: unknown): DockSide => (value === "right" ? "right" : "left");

export const parseStoredDockState = (raw: string | null): StoredDockState | null => {
   if (!raw) return null;
   try {
      const parsed = JSON.parse(raw) as Partial<StoredDockState>;
      return {
         isOpen: parsed.isOpen === true,
         conversationId:
            typeof parsed.conversationId === "string" && parsed.conversationId
               ? parsed.conversationId
               : null,
         side: parseDockSide(parsed.side),
      };
   } catch {
      return null;
   }
};

/**
 * GailGPT dock requires the PostHog flag plus GailGPT chat permission and Gail Agent access.
 * GPT-only orgs/users keep the classic full-page GailGPT experience.
 */
export const computeGailGptDockEnabled = (
   isFeatureEnabled: boolean,
   canAccessGailGptChat: boolean,
   userCanUseGailAgent: boolean
): boolean => isFeatureEnabled && canAccessGailGptChat && userCanUseGailAgent;

/** Dock can open only when GailGPT dock is enabled and the user is not already on a full-page GailGPT route. */
export const computeDockAvailability = (
   isGailGptDockEnabled: boolean,
   pathname: string | null
): boolean => isGailGptDockEnabled && !isGailGptRoute(pathname ?? "");

type GailDockContextValue = {
   isGailGptDockEnabled: boolean;
   isDockAvailable: boolean;
   isOpen: boolean;
   /** Which edge the open dock hugs. A handoff to a resource page uses the right. */
   side: DockSide;
   conversationId: string | null;
   openDock: () => void;
   closeDock: () => void;
   toggleDock: () => void;
   startNewChat: () => void;
   setDockConversation: (conversationId: string) => void;
   /** Opens this conversation in the dock on the right, beside the page it just changed. */
   showConversationOnRight: (conversationId: string, followUp?: string) => void;
   /** Retain the handoff note until a sender accepts it; serialize sends across dock remounts. */
   sendDockFollowUp: (conversationId: string, send: (text: string) => Promise<boolean>) => Promise<void>;
};

const GailDockContext = createContext<GailDockContextValue | null>(null);

export const GailDockProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
   const pathname = usePathname();
   const {productAccess} = useOrg();
   const {allowed: canAccessGailGptChat} = useGailGptChatAccess();

   const [isOpen, setIsOpen] = useState(false);
   const [side, setSide] = useState<DockSide>("left");
   const [conversationId, setConversationId] = useState<string | null>(null);
   const [isHydrated, setIsHydrated] = useState(false);
   const followUpRef = useRef<{conversationId: string; text: string; sending: boolean} | null>(null);

   useEffect(() => {
      try {
         const stored = parseStoredDockState(globalThis.sessionStorage?.getItem(DOCK_STORAGE_KEY) ?? null);
         if (stored) {
            setIsOpen(stored.isOpen);
            setSide(stored.side);
            setConversationId(stored.conversationId);
         }
      } catch {
         // Storage can throw in strict privacy modes.
      }
      setIsHydrated(true);
   }, []);

   useEffect(() => {
      if (!isHydrated) return;
      try {
         globalThis.sessionStorage?.setItem(
            DOCK_STORAGE_KEY,
            JSON.stringify({isOpen, conversationId, side} satisfies StoredDockState)
         );
      } catch {
         // Ignore quota/unavailable storage.
      }
   }, [isHydrated, isOpen, conversationId, side]);

   // Mount-gated so dock vs classic nav markup matches SSR on first paint.
   const isPostHogDockFlagEnabled = useHydrationSafeFeatureFlagEnabled(DOCK_FEATURE_FLAG);

   const isGailGptDockEnabled = computeGailGptDockEnabled(
      isPostHogDockFlagEnabled,
      canAccessGailGptChat,
      productAccess.userCanUseGailAgent
   );

   // The builder shares the conversation session but owns its right-hand
   // workspace pane. It uses the same chat access gate without the dock flag or Gail Agent requirement.
   const isWorkflowBuilder = workflowBuilderRouteId(pathname ?? "") !== null;
   const isDockAvailable = computeDockAvailability(isGailGptDockEnabled, pathname) ||
      (isWorkflowBuilder && canAccessGailGptChat);

   const pinnedApiVersion = useSyncExternalStore(
      subscribeConversationRouting,
      () => conversationId ? getPinnedConversationRouting(conversationId)?.apiVersion ?? null : null,
      () => null
   );
   // Only a confirmed legacy conversation gives way to a new v2 chat.
   // Missing pins must wait for history discovery before the builder decides.
   useEffect(() => {
      if (isWorkflowBuilder && conversationId && pinnedApiVersion === "legacy") {
         setConversationId(null);
      }
   }, [isWorkflowBuilder, conversationId, pinnedApiVersion]);

   const openDock = useCallback(() => setIsOpen(true), []);
   const closeDock = useCallback(() => setIsOpen(false), []);
   const toggleDock = useCallback(() => setIsOpen((prev) => !prev), []);
   const startNewChat = useCallback(() => {
      setConversationId(null);
      setIsOpen(true);
   }, []);
   const setDockConversation = useCallback((id: string) => {
      setConversationId(id);
      setIsOpen(true);
   }, []);
   const showConversationOnRight = useCallback((id: string, followUp?: string) => {
      setConversationId(id);
      setSide("right");
      setIsOpen(true);
      followUpRef.current = followUp ? {conversationId: id, text: followUp, sending: false} : null;
   }, []);
   const sendDockFollowUp = useCallback(async (id: string, send: (text: string) => Promise<boolean>) => {
      const pending = followUpRef.current;
      if (!pending || pending.conversationId !== id || pending.sending) return;
      pending.sending = true;
      try {
         const accepted = await send(pending.text);
         // A newer save may have queued a different note while this send was running.
         if (accepted && followUpRef.current === pending) followUpRef.current = null;
      } finally {
         pending.sending = false;
      }
   }, []);

   useEffect(() => {
      if (!isDockAvailable) return;
      const onKeyDown = (event: KeyboardEvent) => {
         if (
            (event.metaKey || event.ctrlKey) &&
            !event.shiftKey &&
            !event.altKey &&
            event.key.toLowerCase() === "g"
         ) {
            event.preventDefault();
            toggleDock();
         }
      };
      window.addEventListener("keydown", onKeyDown);
      return () => window.removeEventListener("keydown", onKeyDown);
   }, [isDockAvailable, toggleDock]);

   const value = useMemo(
      () => ({
         isGailGptDockEnabled,
         isDockAvailable,
         isOpen,
         side,
         conversationId,
         openDock,
         closeDock,
         toggleDock,
         startNewChat,
         setDockConversation,
         showConversationOnRight,
         sendDockFollowUp,
      }),
      [
         isGailGptDockEnabled,
         isDockAvailable,
         isOpen,
         side,
         conversationId,
         openDock,
         closeDock,
         toggleDock,
         startNewChat,
         setDockConversation,
         showConversationOnRight,
         sendDockFollowUp,
      ]
   );

   return <GailDockContext.Provider value={value}>{children}</GailDockContext.Provider>;
};

const NO_DOCK: GailDockContextValue = {
   isGailGptDockEnabled: false,
   isDockAvailable: false,
   isOpen: false,
   side: "left",
   conversationId: null,
   openDock: () => {},
   closeDock: () => {},
   toggleDock: () => {},
   startNewChat: () => {},
   setDockConversation: () => {},
   showConversationOnRight: () => {},
   sendDockFollowUp: async () => {},
};

export const useGailDock = (): GailDockContextValue => useContext(GailDockContext) ?? NO_DOCK;
